import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { BilledInBadge } from '@/components/ads/BillingCurrency';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { LoadErrorState, NoMatchesState } from '@/components/ListStates';
import { facebookCampaignsService, FbCampaign } from '@/services/facebook-campaigns.service';
import { facebookAdAccountService, MetaAdAccount } from '@/services/facebook-ad-account.service';
import { facebookAdSetsService, FbAdSet } from '@/services/facebook-ad-sets.service';
import { facebookAdsService, FbAd } from '@/services/facebook-ads.service';
import { facebookAdCreativesService } from '@/services/facebook-ad-creatives.service';
import { CampaignCard } from '@/components/facebook/campaigns-list';
import type { AdWithCreative } from '@/components/facebook/campaigns-list';
import { facebookInsightsService, InsightsTrendPoint } from '@/services/facebook-insights.service';
import { AccountHealthBanner } from '@/components/facebook/AccountHealthBanner';
import { MetaScopeBanner } from '@/components/facebook/MetaScopeBanner';
import { CompareCampaignsModal } from '@/components/facebook/CompareCampaignsModal';
import { DateRangePicker, DateRangeValue, dateRangeToParams, describeRange } from '@/components/DateRangePicker';
import { RefreshCw, ExternalLink, Search, BarChart2, Plus, AlertCircle, GitCompare, X, Pause, Play, Archive, Camera } from 'lucide-react';

const STATUS_FILTERS = ['ALL', 'ACTIVE', 'PAUSED', 'ARCHIVED', 'DISAPPROVED'] as const;
type StatusFilter = typeof STATUS_FILTERS[number];

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

type ArchiveTarget =
  | { kind: 'campaign'; entity: FbCampaign }
  | { kind: 'adset'; entity: FbAdSet }
  | { kind: 'ad'; entity: FbAd };

type DeleteTarget = { kind: 'campaign'; entity: FbCampaign };

/**
 * `platformScope` controls which campaigns the list shows:
 *   - 'all'       (default) — every campaign for the ad account. The chip
 *                 filter (All / Facebook / Instagram / Both) is rendered so
 *                 the operator can narrow client-side.
 *   - 'instagram' — only campaigns whose ad sets target IG (IG-only OR
 *                 cross-platform). The platform chip filter is HIDDEN
 *                 because the scope is already locked to IG, and each row
 *                 gets a small badge so cross-platform vs IG-only is
 *                 visible at a glance. Mounted by the /instagram/campaigns
 *                 route via InstagramCampaignsList.
 *   - 'facebook' — reserved for the symmetric FB-only module. Falls back
 *                 to 'all' until that view is built.
 */
export type PlatformScope = 'all' | 'instagram' | 'facebook';

interface Props {
  platformScope?: PlatformScope;
}

export default function FacebookCampaignsList({ platformScope = 'all' }: Props) {
  const [campaigns, setCampaigns] = useState<FbCampaign[] | null>(null);
  const [account, setAccount] = useState<MetaAdAccount | null>(null);
  const [fetchedAt, setFetchedAt] = useState<string | null>(null);
  const [fromCache, setFromCache] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [platformFilter, setPlatformFilter] = useState<'ALL' | 'FB_ONLY' | 'IG_ONLY' | 'BOTH'>('ALL');
  const [dateRange, setDateRange] = useState<DateRangeValue>({ preset: 'last_30d' });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [compareOpen, setCompareOpen] = useState(false);

  // Expanded set + lazy caches
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [adSetsByCampaign, setAdSetsByCampaign] = useState<Map<string, FbAdSet[]>>(new Map());
  const [adsByAdSet, setAdsByAdSet] = useState<Map<string, AdWithCreative[]>>(new Map());
  const [loadingAdSetsFor, setLoadingAdSetsFor] = useState<Set<string>>(new Set());
  const [loadingAdsFor, setLoadingAdsFor] = useState<Set<string>>(new Set());

  // Per-row pending action state
  const [campaignBusy, setCampaignBusy] = useState<Set<string>>(new Set());
  const [adSetBusy, setAdSetBusy] = useState<Set<string>>(new Set());
  const [adBusy, setAdBusy] = useState<Set<string>>(new Set());

  // Per-campaign daily trend (for inline sparklines).
  // undefined = not yet fetched, [] = fetched but empty.
  const [trendsByCampaign, setTrendsByCampaign] = useState<Record<string, InsightsTrendPoint[]>>({});

  // Confirm-archive target
  const [archiveTarget, setArchiveTarget] = useState<ArchiveTarget | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  // Retry repeats whatever failed — a forced live pull or a cached read.
  const lastForce = useRef(false);
  const load = async (force = false) => {
    lastForce.current = force;
    setRefreshing(true);
    setError(null);
    try {
      const r = await facebookCampaignsService.list(force, undefined, dateRangeToParams(dateRange));
      setCampaigns(r.data);
      setFetchedAt(r.fetchedAt);
      setFromCache(r.fromCache);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load campaigns');
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    facebookAdAccountService.get().then(setAccount).catch(() => undefined);
  }, []);

  // Re-load whenever the date range changes
  useEffect(() => {
    load(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange.preset, dateRange.since, dateRange.until]);

  // Fetch per-campaign daily sparkline trends in one bulk call whenever the
  // visible campaign list or date range changes. The endpoint reads from our
  // local snapshot cache, so this is cheap and Meta-rate-limit friendly.
  useEffect(() => {
    if (!campaigns || campaigns.length === 0) {
      setTrendsByCampaign({});
      return;
    }
    const ids = campaigns.map((c) => c.id);
    const ymd = (d: Date) => d.toISOString().slice(0, 10);
    // Use a fixed 30-day window so the sparkline always shows the same shape
    // regardless of the table's date range filter — that filter is for the
    // *aggregate* totals shown in the row, not for the trend.
    const until = ymd(new Date());
    const since = ymd(new Date(Date.now() - 30 * 86400_000));
    facebookInsightsService.campaignTrendsBulk(ids, since, until)
      .then(setTrendsByCampaign)
      .catch(() => undefined); // Non-blocking — the list still renders without trends.
  }, [campaigns]);

  // ─── Lazy loaders ──────────────────────────────────────────────────────────
  const loadAdsForAdSet = useCallback(async (adSetId: string, force = false) => {
    setLoadingAdsFor((s) => new Set(s).add(adSetId));
    try {
      const r = await facebookAdsService.byAdSet(adSetId, force);
      // Try to enrich with creative data; failures are non-fatal.
      const enriched: AdWithCreative[] = await Promise.all(
        r.data.map(async (ad) => {
          if (!ad.creative?.id) return ad;
          try {
            const c = await facebookAdCreativesService.get(ad.creative.id);
            return { ...ad, creative_data: c };
          } catch {
            return ad;
          }
        }),
      );
      setAdsByAdSet((m) => {
        const next = new Map(m);
        next.set(adSetId, enriched);
        return next;
      });
    } catch {
      /* axios interceptor toasts */
    } finally {
      setLoadingAdsFor((s) => {
        const next = new Set(s);
        next.delete(adSetId);
        return next;
      });
    }
  }, []);

  const loadAdSetsForCampaign = useCallback(
    async (campaignId: string, force = false) => {
      setLoadingAdSetsFor((s) => new Set(s).add(campaignId));
      try {
        const r = await facebookAdSetsService.byCampaign(campaignId, force);
        setAdSetsByCampaign((m) => {
          const next = new Map(m);
          next.set(campaignId, r.data);
          return next;
        });
        // Eagerly fetch ads for each adset in parallel. Skip any without an id —
        // a guard against malformed cache rows so we never fire `/by-ad-set/undefined`.
        await Promise.all(
          r.data
            .filter((s): s is typeof s & { id: string } => typeof s.id === 'string' && s.id.length > 0)
            .map((s) => loadAdsForAdSet(s.id, force)),
        );
      } catch {
        /* axios interceptor toasts */
      } finally {
        setLoadingAdSetsFor((s) => {
          const next = new Set(s);
          next.delete(campaignId);
          return next;
        });
      }
    },
    [loadAdsForAdSet],
  );

  const toggleExpand = (campaignId: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(campaignId)) {
        next.delete(campaignId);
      } else {
        next.add(campaignId);
        // Lazy-load adsets the first time we open this card.
        if (!adSetsByCampaign.has(campaignId) && !loadingAdSetsFor.has(campaignId)) {
          void loadAdSetsForCampaign(campaignId);
        }
      }
      return next;
    });
  };

  // ─── Action handlers ───────────────────────────────────────────────────────
  const onCampaignToggle = async (c: FbCampaign) => {
    const isActive = (c.effective_status ?? c.status) === 'ACTIVE';
    setCampaignBusy((s) => new Set(s).add(c.id));
    try {
      if (isActive) await facebookCampaignsService.pause(c.id);
      else await facebookCampaignsService.resume(c.id);
      toast.success(isActive ? 'Campaign paused' : 'Campaign resumed');
      await load(true);
    } catch {
      /* toast shown */
    } finally {
      setCampaignBusy((s) => {
        const next = new Set(s);
        next.delete(c.id);
        return next;
      });
    }
  };

  const onCampaignArchive = (c: FbCampaign) => setArchiveTarget({ kind: 'campaign', entity: c });
  const onCampaignDelete = (c: FbCampaign) => setDeleteTarget({ kind: 'campaign', entity: c });

  // ─── Bulk actions ──────────────────────────────────────────────────────────
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkArchiveOpen, setBulkArchiveOpen] = useState(false);

  const selectedCampaigns = useMemo(
    () => (campaigns ?? []).filter((c) => selectedIds.has(c.id)),
    [campaigns, selectedIds],
  );
  const selectedActiveCount = selectedCampaigns.filter(
    (c) => (c.effective_status ?? c.status) === 'ACTIVE',
  ).length;
  const selectedPausedCount = selectedCampaigns.filter(
    (c) => (c.effective_status ?? c.status) === 'PAUSED',
  ).length;

  /**
   * Run an action across every selected campaign concurrently. Failures on
   * individual campaigns surface as toasts (axios interceptor) but don't
   * abort the rest. Final toast summarises the result.
   */
  const runBulk = async (
    action: 'pause' | 'resume' | 'archive',
    targets: FbCampaign[],
  ) => {
    if (targets.length === 0) return;
    setBulkBusy(true);
    setCampaignBusy((s) => {
      const next = new Set(s);
      for (const c of targets) next.add(c.id);
      return next;
    });
    const results = await Promise.allSettled(targets.map((c) => {
      if (action === 'pause') return facebookCampaignsService.pause(c.id);
      if (action === 'resume') return facebookCampaignsService.resume(c.id);
      return facebookCampaignsService.archive(c.id);
    }));
    const ok = results.filter((r) => r.status === 'fulfilled').length;
    const failed = results.length - ok;
    const verb = action === 'pause' ? 'paused' : action === 'resume' ? 'resumed' : 'archived';
    if (ok > 0) toast.success(`${ok} campaign${ok === 1 ? '' : 's'} ${verb}${failed > 0 ? ` · ${failed} failed` : ''}`);
    else if (failed > 0) toast.error(`All ${failed} bulk ${action}s failed`);
    setCampaignBusy((s) => {
      const next = new Set(s);
      for (const c of targets) next.delete(c.id);
      return next;
    });
    setBulkBusy(false);
    setSelectedIds(new Set());
    await load(true);
  };

  const onBulkPause = () => {
    const targets = selectedCampaigns.filter(
      (c) => (c.effective_status ?? c.status) === 'ACTIVE',
    );
    runBulk('pause', targets);
  };

  const onBulkResume = () => {
    const targets = selectedCampaigns.filter(
      (c) => (c.effective_status ?? c.status) === 'PAUSED',
    );
    runBulk('resume', targets);
  };

  const onBulkArchiveConfirm = () => {
    setBulkArchiveOpen(false);
    runBulk('archive', selectedCampaigns);
  };

  /** Force-refresh insights for a campaign + every ad set + every ad under
   *  it. One click → whole tree synced from Meta, regardless of how many ad
   *  sets or ads it contains. */
  const onCampaignSyncInsights = async (c: FbCampaign) => {
    setCampaignBusy((s) => new Set(s).add(c.id));
    try {
      // 1. Campaign-level insights.
      const fresh = await facebookCampaignsService.get(c.id, true);
      setCampaigns((prev) => (prev ?? []).map((x) => (x.id === c.id ? fresh : x)));
      // 2. Force-refresh ad sets for this campaign — loadAdSetsForCampaign
      //    cascades into ads when force=true.
      await loadAdSetsForCampaign(c.id, true);
      // 3. Auto-expand the row so the user sees the freshly synced tree.
      setExpanded((prev) => new Set(prev).add(c.id));
      toast.success('Synced campaign, ad sets, and ads from Meta');
    } catch {
      /* axios interceptor toasts the error */
    } finally {
      setCampaignBusy((s) => {
        const next = new Set(s);
        next.delete(c.id);
        return next;
      });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const t = deleteTarget;
    setDeleteTarget(null);
    setCampaignBusy((s) => new Set(s).add(t.entity.id));
    try {
      await facebookCampaignsService.remove(t.entity.id);
      toast.success('Campaign deleted from Facebook');
      await load(true);
    } catch {
      /* axios interceptor toasts */
    } finally {
      setCampaignBusy((s) => {
        const n = new Set(s);
        n.delete(t.entity.id);
        return n;
      });
    }
  };

  const onAdSetToggle = async (s: FbAdSet) => {
    const isActive = (s.effective_status ?? s.status) === 'ACTIVE';
    setAdSetBusy((set) => new Set(set).add(s.id));
    try {
      if (isActive) await facebookAdSetsService.pause(s.id);
      else await facebookAdSetsService.resume(s.id);
      toast.success(isActive ? 'Ad set paused' : 'Ad set resumed');
      // Refresh just the campaign's adsets.
      await loadAdSetsForCampaign(s.campaign_id);
    } catch {
      /* toast shown */
    } finally {
      setAdSetBusy((set) => {
        const next = new Set(set);
        next.delete(s.id);
        return next;
      });
    }
  };

  const onAdSetArchive = (s: FbAdSet) => setArchiveTarget({ kind: 'adset', entity: s });

  const onAdToggle = async (a: FbAd) => {
    const isActive = (a.effective_status ?? a.status) === 'ACTIVE';
    setAdBusy((set) => new Set(set).add(a.id));
    try {
      if (isActive) await facebookAdsService.pause(a.id, a.adset_id);
      else await facebookAdsService.resume(a.id, a.adset_id);
      toast.success(isActive ? 'Ad paused' : 'Ad resumed');
      await loadAdsForAdSet(a.adset_id);
    } catch {
      /* toast shown */
    } finally {
      setAdBusy((set) => {
        const next = new Set(set);
        next.delete(a.id);
        return next;
      });
    }
  };

  const onAdArchive = (a: FbAd) => setArchiveTarget({ kind: 'ad', entity: a });

  const confirmArchive = async () => {
    if (!archiveTarget) return;
    const t = archiveTarget;
    setArchiveTarget(null);
    try {
      if (t.kind === 'campaign') {
        setCampaignBusy((s) => new Set(s).add(t.entity.id));
        await facebookCampaignsService.archive(t.entity.id);
        toast.success('Campaign archived');
        await load(true);
      } else if (t.kind === 'adset') {
        setAdSetBusy((s) => new Set(s).add(t.entity.id));
        await facebookAdSetsService.archive(t.entity.id);
        toast.success('Ad set archived');
        await loadAdSetsForCampaign(t.entity.campaign_id);
      } else {
        setAdBusy((s) => new Set(s).add(t.entity.id));
        await facebookAdsService.archive(t.entity.id, t.entity.adset_id);
        toast.success('Ad archived');
        await loadAdsForAdSet(t.entity.adset_id);
      }
    } catch {
      /* toast shown */
    } finally {
      if (t.kind === 'campaign') {
        setCampaignBusy((s) => {
          const n = new Set(s);
          n.delete(t.entity.id);
          return n;
        });
      } else if (t.kind === 'adset') {
        setAdSetBusy((s) => {
          const n = new Set(s);
          n.delete(t.entity.id);
          return n;
        });
      } else {
        setAdBusy((s) => {
          const n = new Set(s);
          n.delete(t.entity.id);
          return n;
        });
      }
    }
  };

  // Meta reports the account currency; until it does, show '—' rather than guess.
  const currency = account?.currency ?? '';

  const filtered = (campaigns ?? []).filter((c) => {
    const st = c.effective_status ?? c.status;
    if (statusFilter !== 'ALL' && st !== statusFilter) return false;
    // Hard scope filter — when this list is mounted as the Instagram
    // module, only campaigns whose ad sets touch IG (IG-only or cross-
    // platform) belong here. Symmetric for the future Facebook-only
    // mount. Campaigns whose ad-sets aren't synced yet (empty platforms)
    // are HIDDEN under a scoped mount so we don't show the operator
    // mystery rows we can't classify.
    if (platformScope === 'instagram') {
      if (!c.platforms || !c.platforms.includes('instagram')) return false;
    } else if (platformScope === 'facebook') {
      if (!c.platforms || !c.platforms.includes('facebook')) return false;
    }
    // Platform filter chip — only meaningful when scope='all'. When the
    // list is already locked to IG (or future FB), we hide the chip and
    // skip this branch.
    if (platformScope === 'all' && platformFilter !== 'ALL' && c.platforms && c.platforms.length > 0) {
      const hasFb = c.platforms.includes('facebook');
      const hasIg = c.platforms.includes('instagram');
      if (platformFilter === 'FB_ONLY' && !(hasFb && !hasIg)) return false;
      if (platformFilter === 'IG_ONLY' && !(!hasFb && hasIg)) return false;
      if (platformFilter === 'BOTH' && !(hasFb && hasIg)) return false;
    }
    if (search) {
      const q = search.toLowerCase();
      return (
        c.name.toLowerCase().includes(q) ||
        c.id.includes(q) ||
        c.objective.toLowerCase().includes(q)
      );
    }
    return true;
  });

  const cacheDesc = fromCache && fetchedAt ? `Cached · last fetch ${relativeTime(fetchedAt)}` : undefined;
  const filtersActive = !!search.trim() || statusFilter !== 'ALL' || (platformScope === 'all' && platformFilter !== 'ALL');
  const clearFilters = () => { setSearch(''); setStatusFilter('ALL'); setPlatformFilter('ALL'); };

  const sortedCampaigns = useMemo(() => {
    const ts = (c: FbCampaign) => {
      const t = c.created_time ? Date.parse(c.created_time) : NaN;
      return Number.isFinite(t) ? t : 0;
    };
    return [...filtered].sort((a, b) => ts(b) - ts(a));
  }, [filtered]);

  const archiveTitle =
    archiveTarget?.kind === 'campaign'
      ? 'Archive campaign?'
      : archiveTarget?.kind === 'adset'
        ? 'Archive ad set?'
        : 'Archive ad?';
  const archiveMessage = archiveTarget
    ? archiveTarget.kind === 'campaign'
      ? `Archive "${archiveTarget.entity.name}"? You can still view it in Ads Manager but it cannot be reactivated from here.`
      : archiveTarget.kind === 'adset'
        ? `Archive "${archiveTarget.entity.name}"? This stops the ad set permanently.`
        : `Archive "${archiveTarget.entity.name}"? This stops the ad permanently.`
    : '';

  // Scoped header — when mounted as /instagram/campaigns the title, breadcrumb,
  // and "New Campaign" CTA should all reflect the Instagram module rather
  // than dump the operator back into the Facebook workspace.
  const isIgScope = platformScope === 'instagram';
  const pageTitle = isIgScope ? 'Instagram Campaigns' : 'Facebook Campaigns';
  const pageBreadcrumb = isIgScope ? 'Instagram / Campaigns' : 'Facebook Ads / Campaigns';
  const newCampaignTarget = isIgScope ? '/instagram/workspace' : '/facebook/workspace';
  // No authorized Page means the workspace would bounce the user back at creative-save time.
  const canCreateCampaign = !account || !!((account.pageIds && account.pageIds.length > 0) || account.pageId);

  return (
    <>
      <Topbar
        title={pageTitle}
        breadcrumb={pageBreadcrumb}
        description={cacheDesc}
        right={
          <div className="flex items-center gap-2">
            <BilledInBadge platform="meta" currency={account?.currency} />
            <DateRangePicker value={dateRange} onChange={setDateRange} />
            <button
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface text-text-secondary hover:bg-surface-alt hover:text-text-primary disabled:opacity-50 transition-colors"
              onClick={() => load(true)}
              disabled={refreshing}
              aria-label="Refresh"
            >
              <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
            </button>
            <a href="https://business.facebook.com/adsmanager" target="_blank" rel="noreferrer">
              <Button variant="ghost" size="sm" iconRight={<ExternalLink size={12} />}>
                Ads Manager
              </Button>
            </a>
            {/* IG campaigns need a Page too — Meta requires a FB Page as the IG actor on every IG ad creative. */}
            {canCreateCampaign ? (
              <Link to={newCampaignTarget}>
                <Button iconLeft={<Plus size={14} />}>New Campaign</Button>
              </Link>
            ) : (
              <Link to="/facebook/ad-account" title="Connect a Facebook Page first to create a campaign">
                <Button iconLeft={<Plus size={14} />} variant="secondary">
                  Connect Page first
                </Button>
              </Link>
            )}
          </div>
        }
      />

      <div className="px-7 py-7 w-full space-y-4">
        <MetaScopeBanner />
        <AccountHealthBanner account={account} />

        {/* A failed refresh over an already-loaded list; a failed first load shows in place of the list. */}
        {error && campaigns !== null && (
          <div className="flex items-start gap-3 rounded-xl border border-danger/30 bg-danger-soft p-4">
            <AlertCircle size={16} className="mt-0.5 shrink-0 text-danger" />
            <p className="flex-1 text-sm text-danger">{error}</p>
            <Button size="sm" variant="secondary" onClick={() => load(lastForce.current)} disabled={refreshing}>Retry</Button>
          </div>
        )}

        {/* Filters card — search on top, segmented filters below, count on the right */}
        <div className="rounded-xl border border-border bg-surface p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[260px] max-w-md">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search campaigns by name, id or objective…"
                iconLeft={<Search size={14} />}
              />
            </div>
            <div className="ml-auto whitespace-nowrap text-xs text-text-muted">
              {filtered.length} campaign{filtered.length === 1 ? '' : 's'} · {describeRange(dateRange).toLowerCase()} insights
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            <div className="flex items-center gap-2">
              <span className="text-2xs uppercase tracking-wide text-text-muted">Status</span>
              <div className="inline-flex overflow-hidden rounded-lg border border-border">
                {STATUS_FILTERS.map((s, idx) => (
                  <button
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    className={`h-8 px-3 text-xs font-semibold transition-colors ${
                      idx > 0 ? 'border-l border-border' : ''
                    } ${
                      statusFilter === s
                        ? 'bg-gold-gradient text-on-primary'
                        : 'bg-surface text-text-muted hover:bg-surface-alt hover:text-text-primary'
                    }`}
                  >
                    {s === 'ALL' ? 'All' : s.charAt(0) + s.slice(1).toLowerCase()}
                  </button>
                ))}
              </div>
            </div>
            {platformScope === 'all' && (
              <div className="flex items-center gap-2">
                <span className="text-2xs uppercase tracking-wide text-text-muted">Platform</span>
                <div className="inline-flex overflow-hidden rounded-lg border border-border">
                  {([
                    { v: 'ALL',     label: 'All' },
                    { v: 'FB_ONLY', label: 'Facebook' },
                    { v: 'IG_ONLY', label: 'Instagram' },
                    { v: 'BOTH',    label: 'Both' },
                  ] as const).map((p, idx) => (
                    <button
                      key={p.v}
                      onClick={() => setPlatformFilter(p.v)}
                      title="Filter by ad-set placements"
                      className={`h-8 px-3 text-xs font-semibold transition-colors ${
                        idx > 0 ? 'border-l border-border' : ''
                      } ${
                        platformFilter === p.v
                          ? 'bg-info-soft text-info'
                          : 'bg-surface text-text-muted hover:bg-surface-alt hover:text-text-primary'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {isIgScope && (
              <div className="flex items-center gap-2 text-xs text-text-muted">
                <Camera size={13} className="text-info" />
                <span>Showing campaigns whose ad sets target Instagram. Cross-platform (FB+IG) campaigns appear here AND under Facebook.</span>
              </div>
            )}
          </div>
        </div>

        {/* Bulk-action bar — shows when any campaigns selected */}
        {selectedIds.size >= 1 && (
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary-soft px-4 py-2.5">
            <span className="text-sm font-medium text-text-primary">
              {selectedIds.size} selected
              <span className="ml-2 text-xs text-text-muted">
                · {selectedActiveCount} active · {selectedPausedCount} paused
              </span>
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <Button size="sm" variant="ghost" iconLeft={<X size={12} />} onClick={() => setSelectedIds(new Set())} disabled={bulkBusy}>
                Clear
              </Button>
              <Button
                size="sm"
                variant="secondary"
                iconLeft={<Pause size={12} />}
                onClick={onBulkPause}
                disabled={bulkBusy || selectedActiveCount === 0}
                title={selectedActiveCount === 0 ? 'No active campaigns selected' : `Pause ${selectedActiveCount} active campaign${selectedActiveCount === 1 ? '' : 's'}`}
              >
                Pause{selectedActiveCount > 0 ? ` (${selectedActiveCount})` : ''}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                iconLeft={<Play size={12} />}
                onClick={onBulkResume}
                disabled={bulkBusy || selectedPausedCount === 0}
                title={selectedPausedCount === 0 ? 'No paused campaigns selected' : `Resume ${selectedPausedCount} paused campaign${selectedPausedCount === 1 ? '' : 's'}`}
              >
                Resume{selectedPausedCount > 0 ? ` (${selectedPausedCount})` : ''}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                iconLeft={<Archive size={12} />}
                onClick={() => setBulkArchiveOpen(true)}
                disabled={bulkBusy}
                className="text-danger hover:bg-danger-soft"
              >
                Archive ({selectedIds.size})
              </Button>
              <Button
                size="sm"
                iconLeft={<GitCompare size={12} />}
                onClick={() => setCompareOpen(true)}
                disabled={bulkBusy || selectedIds.size < 2}
                title={selectedIds.size < 2 ? 'Pick at least 2 to compare' : `Compare ${selectedIds.size} campaigns`}
              >
                Compare
              </Button>
            </div>
          </div>
        )}

        {/* Card stack */}
        {campaigns === null && error ? (
          <LoadErrorState what="your campaigns" message={error} onRetry={() => load(lastForce.current)} />
        ) : campaigns === null ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="skeleton h-36 rounded-2xl" />
            ))}
          </div>
        ) : filtered.length === 0 && filtersActive ? (
          <NoMatchesState onClear={clearFilters} />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={isIgScope ? <Camera size={28} /> : <BarChart2 size={28} />}
            title="No campaigns yet"
            description={
              isIgScope
                ? 'No campaigns in this ad account target Instagram yet. Build one and it will show up here — cross-platform campaigns appear under Facebook too.'
                : 'There are no campaigns in this ad account yet. Build your first one in the campaign workspace.'
            }
            action={
              canCreateCampaign ? (
                <Link to={newCampaignTarget}>
                  <Button iconLeft={<Plus size={14} />}>New Campaign</Button>
                </Link>
              ) : (
                <Link to="/facebook/ad-account">
                  <Button variant="secondary">Connect a Facebook Page first</Button>
                </Link>
              )
            }
          />
        ) : (
          <div className="space-y-3">
            {sortedCampaigns.map((c) => (
              <div key={c.id} className="flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-4 h-4 w-4 shrink-0 rounded border-border"
                  checked={selectedIds.has(c.id)}
                  onChange={(e) => {
                    const next = new Set(selectedIds);
                    if (e.target.checked) next.add(c.id); else next.delete(c.id);
                    setSelectedIds(next);
                  }}
                  title="Select for comparison"
                />
                <div className="flex-1 min-w-0">
                  <CampaignCard
                    key={c.id}
                    campaign={c}
                    currency={currency}
                    expanded={expanded.has(c.id)}
                    onToggleExpand={() => toggleExpand(c.id)}
                    trend={trendsByCampaign[c.id] ?? []}
                    adSets={adSetsByCampaign.get(c.id)}
                    adSetsLoading={loadingAdSetsFor.has(c.id)}
                    adsByAdSet={Object.fromEntries(adsByAdSet.entries())}
                    adsLoading={
                      (adSetsByCampaign.get(c.id) ?? []).some((s) => loadingAdsFor.has(s.id))
                    }
                    isLoading={campaignBusy.has(c.id)}
                    adSetLoadingIds={adSetBusy}
                    adLoadingIds={adBusy}
                    onToggleStatus={() => onCampaignToggle(c)}
                    onSyncInsights={() => onCampaignSyncInsights(c)}
                    onArchive={() => onCampaignArchive(c)}
                    onDelete={() => onCampaignDelete(c)}
                    onAdSetToggleStatus={onAdSetToggle}
                    onAdSetArchive={onAdSetArchive}
                    onAdToggleStatus={onAdToggle}
                    onAdArchive={onAdArchive}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        <CompareCampaignsModal
          open={compareOpen}
          onClose={() => setCompareOpen(false)}
          campaigns={(campaigns ?? []).filter((c) => selectedIds.has(c.id))}
          currency={currency}
        />

        <div className="rounded-xl border border-border bg-surface px-4 py-2.5 text-xs text-text-muted">
          Cached locally · click Refresh to force a live pull from Meta.
        </div>
      </div>

      <ConfirmDialog
        open={!!archiveTarget}
        title={archiveTitle}
        message={archiveMessage}
        danger
        onCancel={() => setArchiveTarget(null)}
        onConfirm={confirmArchive}
      />
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete campaign?"
        message={
          deleteTarget
            ? `Permanently delete "${deleteTarget.entity.name}" from Facebook? This cannot be undone — the campaign will be removed from Meta and from your CRM.`
            : ''
        }
        danger
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />

      <ConfirmDialog
        open={bulkArchiveOpen}
        title={`Archive ${selectedIds.size} campaign${selectedIds.size === 1 ? '' : 's'}?`}
        message={`This will archive ${selectedIds.size} selected campaign${selectedIds.size === 1 ? '' : 's'}. You can still view archived campaigns in Ads Manager, but they cannot be reactivated from here. This action runs in parallel — failures on individual campaigns will be reported but won't block the rest.`}
        danger
        onCancel={() => setBulkArchiveOpen(false)}
        onConfirm={onBulkArchiveConfirm}
      />
    </>
  );
}
